import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { IdParamsType } from 'utils/types';
import { postToast } from 'features/common/utils/postFlash';
import { airbrake } from 'ErrorBoundary';

import { createEventGate, deleteEventGate, getEventGates, getPartnerMembers } from '../services';
import { Gate, PartnerMember } from '../types';

export const gatesKey = (id: string) => ['gates', id];
export const PARTNER_MEMBERS_KEY = ['partnerSettings'];

/** A partner member's display name, with a stable fallback order — shared by the page's scanner chips
 * and the Manage-scanners dialog so the two never disagree on the terminal fallback. */
export const partnerMemberName = (member: PartnerMember): string =>
	member.full_name || member.name || member.email || '';

/**
 * Gate list + create/delete for the green Gates & Scanners sub-page. Owns the `['gates', id]` +
 * `['partnerSettings']` query keys (exported above so the assign/invite dialogs reuse the same keys
 * rather than re-inlining literals); the dark `features/GatesManagement` clone uses the same key shapes.
 * Assign/unassign + invite live in their own dialogs (dialog-local state). (The Scanned-Tickets gate
 * roll-up is a separate surface — it reads the `['event-scans', id]` aggregate, not these keys.)
 */
export const useGatesManagement = () => {
	const { id } = useParams<IdParamsType>();
	const { t } = useTranslation();
	const queryClient = useQueryClient();

	const gatesQuery = useQuery<Gate[]>({ queryKey: gatesKey(id), queryFn: () => getEventGates(id) });
	// The roster resolves scanner names; surface a load failure instead of silently showing "Unknown scanner".
	const membersQuery = useQuery({
		queryKey: PARTNER_MEMBERS_KEY,
		queryFn: getPartnerMembers,
		onError: (error) => {
			airbrake.notify({ error: 'Error loading partner members', context: { error } });
			postToast('error', t('gates-members-error', 'Could not load team members'));
		},
	});

	const invalidateGates = () => queryClient.invalidateQueries({ queryKey: gatesKey(id) });

	const createGate = useMutation({
		mutationFn: (name: string) => createEventGate(id, name),
		onSuccess: () => {
			invalidateGates();
			postToast('success', t('gates-create-success', 'Gate created'));
		},
		onError: (error) => {
			airbrake.notify({ error: 'Error creating gate', context: { error } });
			postToast('error', t('gates-create-error', 'Could not create the gate'));
		},
	});

	const deleteGate = useMutation({
		mutationFn: (gateId: number) => deleteEventGate(id, gateId),
		onSuccess: () => {
			invalidateGates();
			postToast('success', t('gates-delete-success', 'Gate deleted'));
		},
		onError: (error) => {
			airbrake.notify({ error: 'Error deleting gate', context: { error } });
			postToast('error', t('gates-delete-error', 'Could not delete the gate'));
		},
	});

	/** Resolve a scanner's display name from the partner-members roster (scanner_id === member.user_id). */
	const resolveScannerName = (scannerId: number): string => {
		const member = membersQuery.data?.members.find((m) => m.user_id === scannerId);
		return (member && partnerMemberName(member)) || t('gates-unknown-scanner', 'Unknown scanner');
	};

	return { eventId: id, gatesQuery, membersQuery, createGate, deleteGate, resolveScannerName };
};
